import { IdentityVerificationStatusesEnum } from 'types/generated/client';

/** Undefined means neither — an unverified player shows no shield. */
export type VerificationLevel = 'VERIFIED' | 'TRUSTED';

/** The review count that promotes a Verified player to Trusted. Kept as the single
 *  place that decides, and interpolated into the badge copy below. */
export const TRUSTED_REVIEW_THRESHOLD = 15;

/** The word shown beside the shield. */
export const VERIFICATION_LABEL: Record<VerificationLevel, string> = {
  VERIFIED: 'Verified',
  TRUSTED: 'Trusted',
};

/**
 * What each badge means, for the tooltip. Deliberately describes the rule rather
 * than the colour — "gold" and "black" are how the design tells them apart, but a
 * player needs to know what earned it.
 *
 * The wording stays neutral about *how* identity was confirmed: v1 verifies by
 * linking a social account, and the Stripe Identity (ID + selfie) flow is still
 * unbuilt, so naming a method here would date the copy.
 */
export const VERIFICATION_DESCRIPTION: Record<VerificationLevel, string> = {
  VERIFIED: 'This player has a verified identity on Bounce.',
  TRUSTED: `Verified identity, plus ${TRUSTED_REVIEW_THRESHOLD}+ reviews from other players.`,
};

interface VerificationInput {
  /** Rows already filtered to the viewer-visible ones; only VERIFIED is public. */
  identityVerifications?: { status?: string | null }[] | null;
  /** Player reviews received. Absent until player reviews exist. */
  reviewCount?: number | null;
}

/**
 * Verified is identity verification, written when onboarding step 4 completes.
 * Trusted is verified plus the review threshold, derived rather than stored so it
 * follows the count without a backfill.
 */
export const getVerificationLevel = ({
  identityVerifications,
  reviewCount,
}: VerificationInput): VerificationLevel | undefined => {
  const isVerified = (identityVerifications ?? []).some(
    (row) => row.status === IdentityVerificationStatusesEnum.Verified,
  );

  if (!isVerified) return undefined;

  return (reviewCount ?? 0) >= TRUSTED_REVIEW_THRESHOLD ? 'TRUSTED' : 'VERIFIED';
};
